import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toTitleCase } from "@/utils/titleCase";

/**
 * Type-specific "name" fields that sit above the Travelers section in both
 * AddSegmentDialog and EditSegmentDialog:
 *   - Cruise Type (required, cruise only)
 *   - Cruise Line name (cruise only)
 *   - Train Line name (train only)
 *   - Activity / Ticket name (activity/event/site_plan)
 *
 * The block was 100% duplicated between Add and Edit. Extracted here so both
 * dialogs stay in sync automatically.
 *
 * Props:
 *   segment   — current segment state object (must expose `type`,
 *               `cruise_type`, `cruise_line_name`, `train_line_name`,
 *               `activity_name`).
 *   onChange  — (partial) => void. Receives an object to merge into state.
 *               The caller is responsible for spreading the previous state
 *               (same shape both Add and Edit dialogs already use).
 */
export const SegmentTypeNameFields = ({ segment, onChange, testIdPrefix = "segment" }) => {
  if (!segment) return null;
  const type = segment.type;

  return (
    <>
      {/* Cruise Type Selection */}
      {type === 'cruise' && (
        <div className="grid gap-2">
          <Label htmlFor="cruise_type">Cruise Type *</Label>
          <Select
            value={segment.cruise_type || ""}
            onValueChange={(value) => onChange({ cruise_type: value })}
          >
            <SelectTrigger id="cruise_type" data-testid={`${testIdPrefix}-cruise-type-select`}>
              <SelectValue placeholder="Select cruise type..." />
            </SelectTrigger>
            <SelectContent position="popper">
              <SelectItem value="river">River Cruises</SelectItem>
              <SelectItem value="luxury">Luxury or Expedition</SelectItem>
              <SelectItem value="island">Island/Short Cruises</SelectItem>
              <SelectItem value="repositioning">Repositioning Cruises</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-500 dark:text-slate-400">
            This determines the cost allocation percentages (Transportation, Accommodation, Meals &amp; Entertainment)
          </p>
        </div>
      )}

      {/* Cruise Line name (when booked via aggregator) */}
      {type === 'cruise' && (
        <div className="grid gap-2">
          <Label htmlFor="cruise_line_name">Cruise Line (if different from vendor)</Label>
          <Input
            id="cruise_line_name"
            data-testid="segment-cruise-line-name-input"
            placeholder="e.g., Royal Caribbean, Carnival, Disney Cruise Line"
            value={segment.cruise_line_name || ""}
            onChange={(e) => onChange({ cruise_line_name: toTitleCase(e.target.value) })}
          />
          <p className="text-xs text-gray-500 dark:text-slate-400">
            Leave blank if you booked directly with the cruise line.
          </p>
        </div>
      )}

      {/* Train Line (when booked via aggregator like Trainline) */}
      {type === 'train' && (
        <div className="grid gap-2">
          <Label htmlFor="train_line_name">Train Line (if different from vendor)</Label>
          <Input
            id="train_line_name"
            data-testid={`${testIdPrefix}-train-line-name-input`}
            placeholder="e.g., Shinkansen, Eurostar, JR East"
            value={segment.train_line_name || ""}
            onChange={(e) => onChange({ train_line_name: toTitleCase(e.target.value) })}
          />
          <p className="text-xs text-gray-500 dark:text-slate-400">
            Leave blank if the vendor is the train operator.
          </p>
        </div>
      )}

      {/* Activity / ticket name */}
      {(type === 'activity' || type === 'event' || type === 'site_plan') && (
        <div className="grid gap-2">
          <Label htmlFor="activity_name">Activity / Ticket Name</Label>
          <Input
            id="activity_name"
            data-testid={`${testIdPrefix}-activity-name-input`}
            placeholder="e.g., Shibuya Sky Ticket, TeamLab Planets"
            value={segment.activity_name || ""}
            onChange={(e) => onChange({ activity_name: toTitleCase(e.target.value) })}
          />
          <p className="text-xs text-gray-500 dark:text-slate-400">
            The name of the thing you&apos;re doing. Vendor is the site/platform you booked through.
          </p>
        </div>
      )}
    </>
  );
};

export default SegmentTypeNameFields;
